import { Droplets, Beef, PieChart, Sun, BookOpen, Zap, FileText, ClipboardList, Rocket, Monitor, MapPin, Blend, Download } from "lucide-react";

const PILLARS = [
  { icon: Droplets, title: "Insulin and Blood Sugar Mastery" },
  { icon: Beef, title: "The Protein Advantage" },
  { icon: PieChart, title: "The 80/20 Rule" },
  { icon: Sun, title: "Circadian Nutrition and Gut Health", note: "Includes natural GLP-1 boosting tips" },
];

const TIERS = [
  { icon: Monitor, name: "Virtual", text: "Maximum flexibility and elite education entirely online—giving you access to weekly videos, our digital library, and healthy eating masterclasses from your own kitchen." },
  { icon: MapPin, name: "Local", text: "In-person community and professional support hubs." },
  { icon: Blend, name: "Hybrid", text: "The ultimate upgrade for physical hub members, combining in-person check-ins with the Virtual Masterclass Bolt-On as a mid-week momentum builder." },
];

const STEPS = [
  { icon: BookOpen, title: "Member Handbook", text: "To kick off your journey, head over to the Library to download and read the Member Handbook.", handbook: true },
  { icon: Zap, title: "Serious About Training?", text: "If you are consistent with your training, make sure to visit the special 'Athletes' tab and check out the specialist endurance and performance fueling section inside the Member Handbook and inside the Athlete hub." },
  { icon: FileText, title: "Tools & Resources", text: "In the library, you'll also find handy Cheat Sheets, meal ideas, and recipes. These will grow weekly as we add the recipe of the week from the Virtual Club Video." },
  { icon: ClipboardList, title: "Trackers", text: "Don't forget to check out the progress tracker and meal planner/tracker at the very end of the Member Handbook—these can be easily printed off and used as your daily monitoring and planning tools." },
];

export const WelcomeGuide = ({ handbookUrl }) => (
  <div className="space-y-10" data-testid="welcome-guide">
    {/* intro */}
    <div className="max-w-3xl">
      <h2 className="font-heading text-3xl text-navy">Welcome to My Best Body Club!</h2>
      <p className="mt-4 text-[#57534E] leading-relaxed">
        Thank you so much for joining—I am genuinely thrilled to have you here and so excited for you to
        discover how powerful it feels to optimise your nutrition for hormone balance, vibrant health, and
        true longevity.
      </p>
      {handbookUrl && (
        <a href={handbookUrl} target="_blank" rel="noreferrer" data-testid="download-handbook-btn"
          className="mt-5 inline-flex items-center gap-2 bg-teal text-white rounded-full px-7 py-3 text-sm font-medium hover:bg-teal-dark transition-colors">
          <Download className="w-4 h-4" /> Download the Member Handbook
        </a>
      )}
      <p className="mt-4 text-[#57534E] leading-relaxed">
        Traditional slimming clubs have helped millions over the years, but My Best Body Club was born to
        take your results to the next level. Think of us as a <strong className="text-navy">health club with an improved body
        composition side effect</strong>. While traditional clubs often rely on highly processed, chemically altered
        'diet' products and Ultra-Processed Foods (UPFs) just because they are low-calorie, we do the exact
        opposite. We don't look at food through the lens of restriction, nor do we use negative connotations
        like 'Syns'. Instead, our clubs are run by trained health professionals delivering science-backed,
        health-promoting advice focused on nutritious whole foods.
      </p>
    </div>

    {/* pillars */}
    <div>
      <p className="max-w-3xl text-[#57534E] leading-relaxed">
        Your body is a complex biological system that responds to hormones, stress, light, and nutrients.
        To help you navigate this, everything we do is anchored by our <strong className="text-navy">4 Pillars</strong>:
      </p>
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PILLARS.map((p, i) => (
          <div key={p.title} className="bg-navy rounded-2xl p-5 text-white" data-testid={`pillar-${i + 1}`}>
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-xl bg-teal/25 text-teal flex items-center justify-center"><p.icon className="w-5 h-5" /></div>
              <span className="font-heading text-3xl text-white/20">{i + 1}</span>
            </div>
            <h4 className="mt-4 font-heading text-lg leading-snug">{p.title}</h4>
            {p.note && <p className="mt-1.5 text-xs text-teal">{p.note}</p>}
          </div>
        ))}
      </div>
    </div>

    {/* tiers */}
    <div>
      <h3 className="font-heading text-2xl text-navy">Your Membership Tier Options</h3>
      <div className="mt-5 grid sm:grid-cols-3 gap-5">
        {TIERS.map((t) => (
          <div key={t.name} className="bg-white border border-[#E7E5E4] rounded-2xl p-6 shadow-sm" data-testid={`tier-${t.name.toLowerCase()}`}>
            <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center"><t.icon className="w-5 h-5" /></div>
            <h4 className="mt-4 font-heading text-lg text-navy">{t.name}</h4>
            <p className="mt-1.5 text-sm text-[#57534E] leading-relaxed">{t.text}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-[#78716C] italic leading-relaxed max-w-3xl">
        We are expanding our Virtual Membership option from January 2027 to include the physical attendance
        membership classes run by qualified Nutritional Therapists where you can get the data about your body
        composition that weighing scales alone do not provide. We will also be offering the 'Hybrid' option;
        the best of both worlds.
      </p>
    </div>

    {/* first steps */}
    <div>
      <h3 className="font-heading text-2xl text-navy">Getting Started: Your First Steps</h3>
      <div className="mt-5 space-y-4">
        {STEPS.map((s, i) => (
          <div key={s.title} className="flex items-start gap-4 bg-white border border-[#E7E5E4] rounded-xl p-5" data-testid={`step-${i + 1}`}>
            <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0"><s.icon className="w-5 h-5" /></div>
            <div>
              <h4 className="font-heading text-navy">{s.title}</h4>
              <p className="mt-1 text-sm text-[#57534E] leading-relaxed">{s.text}</p>
              {s.handbook && handbookUrl && (
                <a href={handbookUrl} target="_blank" rel="noreferrer" data-testid="step-handbook-link"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal-dark">
                  <Download className="w-3.5 h-3.5" /> Download the Member Handbook
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* coming next */}
    <div className="bg-teal/10 border border-teal/20 rounded-2xl p-6 flex items-start gap-4" data-testid="coming-next">
      <div className="w-11 h-11 rounded-xl bg-teal text-white flex items-center justify-center shrink-0"><Rocket className="w-5 h-5" /></div>
      <div>
        <h4 className="font-heading text-lg text-navy">What's Coming Next</h4>
        <p className="mt-1 text-sm text-[#57534E] leading-relaxed">
          Watch this space! We are constantly innovating, with future roll-outs including our official
          <strong className="text-navy"> My Best Body Club App</strong> and custom-printed recipe books. Stay tuned for updates, and let's dive in!
        </p>
      </div>
    </div>
  </div>
);
